import {Abilities} from '../../../../components/enums';
import type {Ability} from '../../../../generated/types';
import {ABILITIES, DEFAULT_ABILITY_CAP, isAbility} from './abilities';
import type {ChoiceInstance} from './feature-tree';
import type {PropsSource} from './props-sources';

type AbilityIncrease = {
	ability: Ability;
	amount: number;
	cap: number;
	/** выбор игрока, который дал повышение: его можно исправить */
	choiceKey?: string;
};

/** повышения не помещаются в предел: без предела значение было бы total */
export type AbilityOverflow = {
	ability: Ability;
	total: number;
	cap: number;
	/** выборы, которые повышают эту характеристику: исправить можно любой */
	choiceKeys: string[];
};

/** характеристики, где выбранные повышения упираются в предел и часть очков пропадает */
export function abilityOverflows(
	baseScores: Record<Ability, number>,
	propsSources: PropsSource[],
	choices: ChoiceInstance[],
): AbilityOverflow[] {
	const increases = [...increasesFromProps(propsSources), ...increasesFromChoices(choices)];
	return ABILITIES.flatMap((ability) => {
		const own = increases.filter((increase) => increase.ability === ability);
		const choiceKeys = own.flatMap((increase) => (increase.choiceKey ? [increase.choiceKey] : []));
		if (!choiceKeys.length) return [];
		let score = baseScores[ability];
		let total = score;
		let overflowCap: number | undefined;
		for (const {amount, cap} of own) {
			total += amount;
			if (score + amount > cap) overflowCap = cap;
			score = Math.max(score, Math.min(cap, score + amount));
		}
		return overflowCap === undefined ? [] : [{ability, total, cap: overflowCap, choiceKeys}];
	});
}

export function calculateAbilityScores(
	baseScores: Record<Ability, number>,
	propsSources: PropsSource[],
	choices: ChoiceInstance[],
): Record<Ability, number> {
	const scores = {...baseScores};
	const increases = [...increasesFromProps(propsSources), ...increasesFromChoices(choices)];
	for (const {ability, amount, cap} of increases) {
		scores[ability] = Math.max(scores[ability], Math.min(cap, scores[ability] + amount));
	}
	return scores;
}

export function withAbilityMinimums(
	scores: Record<Ability, number>,
	propsSources: PropsSource[],
): Record<Ability, number> {
	const result = {...scores};
	for (const {props} of propsSources) {
		for (const ability of ABILITIES) {
			const minimum = props.abilityMinimum?.[ability];
			if (minimum) result[ability] = Math.max(result[ability], minimum);
		}
	}
	return result;
}

function increasesFromProps(propsSources: PropsSource[]): AbilityIncrease[] {
	return propsSources.flatMap(({props}) => {
		const {cap = DEFAULT_ABILITY_CAP, ...amounts} = props.abilityIncrease || {};
		return ABILITIES.filter((ability) => amounts[ability]).map((ability) => ({
			ability,
			amount: amounts[ability]!,
			cap,
		}));
	});
}

function increasesFromChoices(choices: ChoiceInstance[]): AbilityIncrease[] {
	return choices
		.filter((choice) => choice.group.type === 'ability' && choice.group.params.increase)
		.flatMap((choice) => {
			const {increase = 0, cap = DEFAULT_ABILITY_CAP} = choice.group.params;
			return choice.selected
				.filter(isAbility)
				.map((ability) => ({ability, amount: increase, cap, choiceKey: choice.key}));
		});
}

/** ошибка выбора повышения: выбранная характеристика превысит предел */
export function abilityChoiceError(choice: ChoiceInstance, overflows: AbilityOverflow[]): string | undefined {
	const messages = overflows
		.filter((overflow) => overflow.choiceKeys.includes(choice.key))
		.map(({ability, total, cap}) => `${Abilities[ability] || ability}: ${total} больше предела ${cap}`);
	return messages.length ? messages.join('; ') : undefined;
}
